import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

export type CanonicalSignupPlatform =
  | 'ios'
  | 'android'
  | 'mobile_web'
  | 'desktop_web'
  | 'unknown';

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function getPlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

function hasMobileUserAgent(ua: string): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
}

function hasCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(pointer: coarse)').matches;
}

export function isLikelyMobileWebClient(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';
  if (hasMobileUserAgent(ua)) {
    return true;
  }

  if (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1 && hasCoarsePointer()) {
    return true;
  }

  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  };

  if (typeof navigatorWithUserAgentData.userAgentData?.mobile === 'boolean') {
    return navigatorWithUserAgentData.userAgentData.mobile;
  }

  return false;
}

/** True inside the native app or a mobile web browser (as opposed to desktop web). */
export function isMobileClient(): boolean {
  return isNativePlatform() || isLikelyMobileWebClient();
}

/**
 * Open a URL outside the app. On native, routes through SFSafariViewController /
 * Chrome Custom Tabs so the user stays in-app; on web, opens a new tab.
 * Returns a Promise that resolves once the OS has handed off the URL.
 *
 * Always use this instead of `window.open(url, '_blank', ...)` — the latter
 * silently fails inside Capacitor's WKWebView.
 */
export async function openExternal(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
    return;
  }
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Opens the iOS Settings app (e.g. when push permission is denied). */
export function openIosSettings(): void {
  if (getPlatform() !== 'ios' || typeof window === 'undefined') return;
  window.location.href = 'app-settings:';
}

export function getSignupPlatform(): CanonicalSignupPlatform {
  if (isNativePlatform()) {
    const nativePlatform = getPlatform();
    if (nativePlatform === 'ios' || nativePlatform === 'android') {
      return nativePlatform;
    }
    return 'unknown';
  }

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'unknown';
  }

  return isLikelyMobileWebClient() ? 'mobile_web' : 'desktop_web';
}
